<script setup lang="ts">
import BooleanIcon from '@/components/shared/BooleanIcon.vue';
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog.vue';
import ButtonContent from '@/components/devextreme/ButtonContent.vue';
import SlaEditPopup from '@/components/maintenance/SlaEditPopup.vue';
import { useMaintenanceDelete } from '@/composables/useMaintenanceDelete';
import { RoleGroups } from '@/constants/Roles';
import Routes from '@/constants/Routes';
import { t } from '@/i18n';
import { useSlaService, dataSource, type SlaKey } from '@/services/api/slaService';
import type { ICarrierSlaResponse } from '@/types/interfaces/MaintenanceTypes';
import Icons from '@/constants/Icons';
import DxButton from 'devextreme-vue/button';
import { DxColumn, DxDataGrid, DxItem, DxToolbar } from 'devextreme-vue/data-grid';
import { ref, shallowRef, onMounted } from 'vue';
import { useRoute } from 'vue-router';

definePage({
  name: 'MaintenanceSla',
  meta: {
    title: 'maintenance.tabs.slas',
    roles: RoleGroups.Maintenance
  }
});

const route = useRoute(Routes.Maintenance.Sla);
const api = useSlaService();
const slasGridRef = shallowRef<DxDataGrid>();
const slaEditPopupRef = ref<typeof SlaEditPopup>();
const deleteDialogRef = ref<InstanceType<typeof DeleteConfirmationDialog>>();

async function confirmDelete(data: ICarrierSlaResponse): Promise<void> {
  const key: SlaKey = {
    carrierCode: data.carrierCode,
    stationCode: data.stationCode
  };

  await deleteDialogRef?.value?.show(key, `${key.carrierCode} - ${key.stationCode}`);
}

const deleteSla = useMaintenanceDelete({
  dataSource,
  dialog: () => deleteDialogRef.value,
  remove: (key: SlaKey) => api.deleteSla(key.carrierCode, key.stationCode),
  key: key => key,
  removedMessage: ({ carrierCode, stationCode }) =>
    t('notifications.slaRemoved', { carrier: carrierCode.trim().toUpperCase(), station: stationCode.trim().toUpperCase() })
});

async function updateData(data: ICarrierSlaResponse, key?: SlaKey | null): Promise<void> {
  dataSource.upsert(data, key);
}

onMounted(() => {
  if (route.params.carrierCode) {
    const code = route.params.carrierCode.trim().toUpperCase();
    slasGridRef.value?.instance?.columnOption('carrierCode', 'filterValues', [ code ]);
  }
});
</script>

<template>
  <SlaEditPopup ref="slaEditPopupRef" @update="updateData" />
  <DeleteConfirmationDialog ref="deleteDialogRef" :entity="$t('entities.sla')" @confirm="deleteSla" />

  <DxDataGrid id="slasGrid" ref="slasGridRef" :data-source="dataSource">
    <DxToolbar>
      <DxItem location="before" template="title-template" />
      <DxItem location="before" template="add-button-template" />
      <DxItem location="after" template="refresh-button-template" />
      <DxItem :locate-in-menu="'auto'" location="after" name="searchPanel" />
    </DxToolbar>

    <DxColumn :sort-index="0" :sort-order="'asc'" :caption="$t('labels.carrier')" data-field="carrierCode" width="150px" />
    <DxColumn :sort-index="1" :sort-order="'asc'" :caption="$t('labels.station')" data-field="stationCode" width="150px" />
    <DxColumn :hiding-priority="0" cell-template="boolean-cell-template" :caption="$t('labels.handleCargo')" data-field="handleCargo"
        :false-text="$t('common.no')" :true-text="$t('common.yes')" />
    <DxColumn :hiding-priority="2" cell-template="boolean-cell-template" :caption="$t('labels.handleTransport')" data-field="handleTransport"
        :false-text="$t('common.no')" :true-text="$t('common.yes')" />
    <DxColumn :hiding-priority="1" :caption="$t('labels.exportStartBeforeMinutes')" data-field="exportStartBeforeMinutes" />
    <DxColumn :hiding-priority="3" cell-template="boolean-cell-template" data-field="enabled" :false-text="$t('common.no')"
        :true-text="$t('common.yes')" width="150px" />
    <DxColumn :visible="false" type="adaptive" />
    <DxColumn :alignment="'center'" :allow-filtering="false" :allow-sorting="false" caption="" cell-template="command-cell-template"
        name="command-column" width="100px" />

    <template #title-template>
      <span class="h3 fw-semibold">{{ $t('maintenance.maintain', { entities: $t('maintenance.tabs.slas') }) }}</span>
    </template>

    <template #add-button-template>
      <DxButton class="ms-3" type="success" @click="slaEditPopupRef?.addSla()">
        <ButtonContent :icon="Icons.Plus" :text="$t('common.addEntity', { entity: $t('entities.sla') })" />
      </DxButton>
    </template>

    <template #refresh-button-template>
      <DxButton type="primary" @click="slasGridRef?.instance?.refresh()">
        <ButtonContent :icon="Icons.ArrowsRotate" />
      </DxButton>
    </template>

    <template #boolean-cell-template="{ data }">
      <BooleanIcon :value="data.value" />
    </template>

    <template #command-cell-template="{ data }">
      <DxButton type="primary" @click="slaEditPopupRef?.editSla({ carrierCode: data.data.carrierCode, stationCode: data.data.stationCode})">
        <ButtonContent :icon="Icons.Pencil" />
      </DxButton>
      <DxButton type="danger" @click="confirmDelete(data.data)">
        <ButtonContent :icon="Icons.Trash" />
      </DxButton>
    </template>
  </DxDataGrid>
</template>

<style scoped>

</style>